import type { NavGroup } from './org-page-redesign/types';

/**
 * Nav groups for the organization workspace rail. Shared between the org
 * detail page (sections rendered in-page) and the resource workspace layout
 * (which navigates back to the detail page's section), so the rail is one
 * definition rather than two that drift.
 *
 * Started as the old two rows of tabs (8 top-level + nested sub-tabs) rendered
 * as a rail — today's taxonomy verbatim, only the container changed (GDN-2566).
 *
 * Regrouped in GDN-2774: "Data" had become the drawer everything got dropped
 * into — revenue-cycle work, bulk moves, and metrics all shared one heading, so
 * none of them read as belonging anywhere. The groups now name what the entries
 * are *for*: JotBill is the revenue-cycle surface (its config stays in
 * Configuration alongside the other product toggles), Import/Export is every
 * bulk data move, Resources is everything you browse, and Data is the
 * measurement left over.
 */
export const ORG_NAV_GROUPS: NavGroup[] = [
  {
    label: 'Organization',
    items: [
      { key: 'overview', label: 'Overview' },
      { key: 'members', label: 'Members' },
      { key: 'subscription', label: 'Subscription' },
      { key: 'activity', label: 'Activity' },
    ],
  },
  {
    label: 'Configuration',
    items: [
      { key: 'settings', label: 'Settings' },
      { key: 'bundles', label: 'Bundles' },
      { key: 'feature-gates', label: 'Feature Gates' },
      { key: 'policies', label: 'Policies' },
      { key: 'dosespot', label: 'JotRX' },
      { key: 'jotaudit', label: 'JotAudit' },
      { key: 'billing', label: 'JotBill' },
    ],
  },
  {
    label: 'Resources',
    items: [
      { key: 'templates', label: 'Templates' },
      { key: 'encounter-types', label: 'Encounter Types' },
      { key: 'clinic-docs', label: 'Clinic Documents' },
      { key: 'resource-explorer', label: 'Resource Explorer' },
      { key: 'shadow-notes', label: 'Shadow Notes', external: true },
    ],
  },
  {
    label: 'JotBill',
    items: [
      { key: 'billing-worklist', label: 'Worklist' },
      { key: 'billing-tasks', label: 'Billing Tasks' },
      { key: 'intake-hub', label: 'Intake Hub' },
      { key: 'payer-docs', label: 'Payer Docs' },
    ],
  },
  {
    label: 'Import/Export',
    items: [
      { key: 'data-import', label: 'Data Import' },
      { key: 'data-export', label: 'Data Export' },
      { key: 'data-migrations', label: 'Patient Data Import', external: true },
    ],
  },
  {
    label: 'Data',
    items: [{ key: 'metrics', label: 'Metrics' }],
  },
];

/**
 * Rail entries that navigate to another page instead of selecting a section.
 *
 * `resource-explorer` joined these when resources moved to their own routes
 * (GDN-2723) — the org detail page no longer renders anything for that key, so
 * treating it as a section lands on a blank tab.
 *
 * `billing-worklist` left them in GDN-2774: as a link it navigated away from
 * the org workspace, which swapped the org rail for the global one and made an
 * org-scoped page read as a global one. It is a section of the org detail page
 * now, so the rail stays put.
 */
export const ORG_NAV_LINK_KEYS = new Set([
  'shadow-notes',
  'data-migrations',
  'resource-explorer',
]);

/** Rail keys that are in-page sections of the org detail view. */
export const ORG_SECTION_KEYS = new Set(
  ORG_NAV_GROUPS.flatMap(g => g.items.map(i => i.key)).filter(
    k => !ORG_NAV_LINK_KEYS.has(k),
  ),
);

/**
 * Where a link-like rail entry goes, for one organization.
 *
 * Both the org detail page and the resource workspace render this rail, and
 * each used to compute destinations itself — which is exactly how
 * `resource-explorer` ended up working from one and dead-ending from the
 * other. One definition, two callers.
 */
export function orgRailDestination(
  key: string,
  organizationId: string,
): string | null {
  switch (key) {
    case 'resource-explorer':
      return `/admin-v2/organization/${encodeURIComponent(organizationId)}/resources`;
    case 'data-migrations':
      return `/admin-v2/organization/${encodeURIComponent(organizationId)}/data-migrations`;
    case 'shadow-notes':
      return `/admin-v2/shadow-notes?organizationId=${encodeURIComponent(organizationId)}`;
    default:
      return null;
  }
}
